import type { LocationQueryRaw, RouteLocationNamedRaw } from 'vue-router'

import { ROUTE_NAMES } from '@/router/constants'
import { type APIGameType, type MainCategoriesType, type PlatformType, type ShortGameType } from '@/types'

import twoPageGameHrus from './2page-games.json'

export const TWO_PAGE_PARTNER = '2page'
const DIGIT_TO_WORD: Record<string, string> = {
    '0': 'zero',
    '1': 'one',
    '2': 'two',
    '3': 'three',
    '4': 'four',
    '5': 'five',
    '6': 'six',
    '7': 'seven',
    '8': 'eight',
    '9': 'nine',
}

const twoPageGames = twoPageGameHrus as string[]
const twoPageGameSet = new Set(twoPageGames)
const twoPageSlugByHru = new Map<string, string>()
const twoPageHruBySlug = new Map<string, string>()
const twoPageCategorySlugByHru = new Map<string, string>()
const twoPageCategoryHruBySlug = new Map<string, string>()
const twoPageCategoryByHru = new Map<string, TwoPageCategory>()
const twoPageCategoryByGameHru = new Map<string, TwoPageCategory>()

type TwoPageCategory = {
    hru: string
    title: string
    categoryId: MainCategoriesType
    games: string[]
}

const twoPageCategories: TwoPageCategory[] = [
    {
        hru: 'simulation',
        title: 'Simulation',
        categoryId: 'simulation',
        games: twoPageGames.slice(0, 92),
    },
    {
        hru: 'racing',
        title: 'Racing',
        categoryId: 'car',
        games: twoPageGames.slice(92, 106),
    },
    {
        hru: 'puzzle',
        title: 'Puzzle',
        categoryId: 'puzzle',
        games: twoPageGames.slice(106, 246),
    },
    {
        hru: 'match',
        title: 'Match',
        categoryId: 'puzzle',
        games: twoPageGames.slice(246, 255),
    },
    {
        hru: 'tabletop',
        title: 'Tabletop',
        categoryId: 'solitaire',
        games: twoPageGames.slice(255),
    },
]

buildGameSlugMaps()
buildCategorySlugMaps()

export function getTwoPageCatalogHRUs() {
    return twoPageGames
}

export function getTwoPageCatalogCategories() {
    return twoPageCategories
}

export function isTwoPageGameHru(hru: string) {
    return twoPageGameSet.has(hru)
}

export function getTwoPageSlugByHru(hru: string) {
    return twoPageSlugByHru.get(hru)
}

export function getTwoPageHruBySlug(slug: string) {
    return twoPageHruBySlug.get(slug)
}

export function getTwoPageCategorySlugByHru(categoryHru: string) {
    return twoPageCategorySlugByHru.get(categoryHru)
}

export function getTwoPageCategoryHruBySlug(slug: string) {
    return twoPageCategoryHruBySlug.get(slug)
}

export function getTwoPageCategoryByHru(categoryHru: string) {
    return twoPageCategoryByHru.get(categoryHru)
}

export function getTwoPageGameCategoryInfo(gameHru: string) {
    return twoPageCategoryByGameHru.get(gameHru)
}

export function buildTwoPageGameRoute(gameHru: string, query?: LocationQueryRaw): RouteLocationNamedRaw {
    return {
        name: ROUTE_NAMES.WHITE_LABEL_2PAGE_GAME,
        params: {
            gameSlug: getTwoPageSlugByHru(gameHru),
        },
        query,
    }
}

export function buildTwoPageLaunchRoute(gameHru: string, query?: LocationQueryRaw): RouteLocationNamedRaw {
    return {
        name: ROUTE_NAMES.WHITE_LABEL_2PAGE_LAUNCH,
        params: {
            gameSlug: getTwoPageSlugByHru(gameHru),
        },
        query,
    }
}

export function buildTwoPageCategoryRoute(categoryHru: string, query?: LocationQueryRaw): RouteLocationNamedRaw {
    return {
        name: ROUTE_NAMES.WHITE_LABEL_2PAGE_CATEGORY,
        params: {
            categorySlug: getTwoPageCategorySlugByHru(categoryHru) || normalizeTwoPageSlug(categoryHru),
        },
        query,
    }
}

export function buildTwoPageExportRoute(
    gameHru: string,
    clid: string,
    query?: LocationQueryRaw,
): RouteLocationNamedRaw {
    return {
        name: ROUTE_NAMES.EXPORT_GAME_PAGE,
        params: {
            locale: '',
            game: gameHru,
        },
        query: {
            ...query,
            export_mode: 'wl',
            clid,
        },
    }
}

export function mapGameToTwoPageCard(game: APIGameType): ShortGameType & { slug: string; mainCategory: string } {
    const platforms: PlatformType[] = []

    if (game.is_desktop) {
        platforms.push('desktop')
    }
    if (game.is_android) {
        platforms.push('android')
    }
    if (game.is_ios) {
        platforms.push('ios')
    }

    return {
        hru: game.hru,
        slug: getTwoPageSlugByHru(game.hru) || normalizeTwoPageSlug(game.hru),
        mainCategory: getTwoPageGameCategoryInfo(game.hru)?.hru || 'puzzle',
        title: game.title,
        preview: game.preview,
        icon: game.icon,
        badges: game.badges,
        source: game.source || 'playgama',
        platforms: platforms.length ? platforms : undefined,
    }
}

function buildGameSlugMaps() {
    const baseCounts = new Map<string, number>()

    for (const hru of twoPageGames) {
        const baseSlug = normalizeTwoPageSlug(hru)

        if (!baseSlug) {
            throw new Error(`Cannot build 2page slug for "${hru}"`)
        }

        const index = baseCounts.get(baseSlug) || 0
        const slug = `${baseSlug}${index > 0 ? numberToLetters(index) : ''}`

        baseCounts.set(baseSlug, index + 1)
        twoPageSlugByHru.set(hru, slug)
        twoPageHruBySlug.set(slug, hru)
    }
}

function buildCategorySlugMaps() {
    for (const category of twoPageCategories) {
        registerTwoPageCategorySlug(category.hru)
        twoPageCategoryByHru.set(category.hru, category)

        for (const gameHru of category.games) {
            twoPageCategoryByGameHru.set(gameHru, category)
        }
    }
}

function registerTwoPageCategorySlug(categoryHru: string) {
    const existingSlug = twoPageCategorySlugByHru.get(categoryHru)
    if (existingSlug) {
        return existingSlug
    }

    const baseSlug = normalizeTwoPageSlug(categoryHru)

    if (!baseSlug) {
        throw new Error(`Cannot build 2page category slug for "${categoryHru}"`)
    }

    let slug = baseSlug
    let index = 0

    while (twoPageCategoryHruBySlug.has(slug) && twoPageCategoryHruBySlug.get(slug) !== categoryHru) {
        index += 1
        slug = `${baseSlug}${numberToLetters(index)}`
    }

    twoPageCategorySlugByHru.set(categoryHru, slug)
    twoPageCategoryHruBySlug.set(slug, categoryHru)

    return slug
}

function normalizeTwoPageSlug(hru: string) {
    return hru
        .toLowerCase()
        .replace(/[0-9]/g, (digit) => DIGIT_TO_WORD[digit] || '')
        .replace(/[^a-z]/g, '')
}

function numberToLetters(index: number) {
    let value = index
    let result = ''

    while (value > 0) {
        value -= 1
        result = String.fromCharCode(97 + (value % 26)) + result
        value = Math.floor(value / 26)
    }

    return result
}
